/**
 * Catálogo de recursos de Culturethèque (médiathèque numérique del Institut
 * français) clasificado por nivel MCER, para que Galia recomiende lecturas,
 * livres audio y demás recursos adaptados al nivel del estudiante.
 *
 * Datos: src/config/culturethequeCatalog.json, generado por
 * scripts/culturetheque-catalog.mjs a partir del catálogo PÚBLICO de
 * culturetheque.com (facetas de nivel del buscador). Para refrescarlo:
 *   node scripts/culturetheque-catalog.mjs
 *
 * El estudiante accede al recurso con SU PROPIA cuenta Culturethèque
 * (gratuita para estudiantes de la Alianza Francesa); Galia solo recomienda
 * y entrega el enlace.
 */

import type { MCERLevel } from '../types';
import catalog from './culturethequeCatalog.json';

interface CtItem {
  title: string;
  url: string;
  type: string;
  authors: string;
  levels: string[];
  description: string;
}

/** Grupos de la faceta "niveau" que cubren cada nivel exacto. */
const GROUP_FOR_LEVEL: Record<MCERLevel, string> = {
  A1: 'A1-A2',
  A2: 'A1-A2',
  B1: 'B1-B2',
  B2: 'B1-B2',
  C1: 'C1-C2',
  C2: 'C1-C2',
};

/** Recursos adecuados para un nivel: faceta exacta o grupo que lo cubre. */
export function culturethequeItemsForLevel(level: MCERLevel): CtItem[] {
  const group = GROUP_FOR_LEVEL[level];
  // C2 no existe como faceta exacta: usa C1 + el grupo avanzado.
  const exact = level === 'C2' ? 'C1' : level;
  return (catalog.items as CtItem[]).filter(
    (i) => i.levels.includes(exact) || i.levels.includes(group),
  );
}

function itemLine(i: CtItem): string {
  const meta = [i.type, i.authors].filter(Boolean).join(', ');
  const desc = i.description ? ` — ${i.description.slice(0, 130)}` : '';
  return `• ${i.title}${meta ? ` (${meta})` : ''}${desc} → ${i.url}`;
}

/**
 * Bloque <culturetheque> del system prompt, filtrado por nivel.
 * Conocimiento latente: Galia recomienda cuando es pertinente, no recita.
 */
export function buildCulturethequeBlock(level: MCERLevel): string {
  const items = culturethequeItemsForLevel(level);
  if (items.length === 0) return '';

  const lines = items.map(itemLine).join('\n');

  return `Tu connais aussi CULTURETHÈQUE (culturetheque.com), la médiathèque numérique de l'Institut français : livres, livres audio, bandes dessinées, presse et podcasts en français. L'accès est gratuit pour les étudiants de l'Alliance Française : chacun se connecte avec SON PROPRE compte Culturethèque (inscription via son Alliance).
Utilise cette connaissance ainsi :
- Si l'étudiant veut lire, écouter ou pratiquer en dehors de Galia (« je veux lire quelque chose », « un livre pour mon niveau », « des podcasts en français »...), RECOMMANDE 1 à 3 ressources de la liste ci-dessous, adaptées à ses goûts.
- Format d'une recommandation : « Titre » (type) + une phrase sur pourquoi ça lui plairait + le lien SEUL sur la ligne suivante. Jamais le lien au milieu d'une phrase.
- En mode Profe, tu peux proposer une lecture de la liste comme prolongement d'un sujet travaillé.
- Recommande UNIQUEMENT des ressources de cette liste (elles correspondent au niveau ${level}) — n'invente jamais de titre ni de lien. Si rien ne convient, dis que le catalogue complet est sur culturetheque.com.
- Si l'étudiant te dit qu'un lien ne marche pas (« contenu non trouvé »), excuse-toi brièvement : l'Institut français retire parfois des ressources du catalogue. Propose tout de suite UNE alternative de la liste.
- Connaissance LATENTE : ne récite jamais ce catalogue spontanément.
RESSOURCES CULTURETHÈQUE DU NIVEAU ${level} :
${lines}`;
}

/**
 * Catálogo COMPLETO (todos los niveles) para el modo profesor (aide-prof).
 * El profesor pide búsquedas transversales ("3 libros A2-B1 sobre X"), así
 * que aquí no se filtra por nivel: cada línea lleva sus niveles etiquetados.
 */
export function buildCulturethequeCatalogueForTeachers(): string {
  const lines = (catalog.items as CtItem[])
    .map((i) => `[${i.levels.join('/')}] ${itemLine(i)}`)
    .join('\n');

  return `# CATALOGUE CULTURETHÈQUE (pour tes recommandations au professeur)
Tu connais le catalogue de Culturethèque (culturetheque.com), la médiathèque numérique de l'Institut français — accès gratuit pour les étudiants et professeurs de l'Alliance avec leur propre compte. Chaque ressource ci-dessous est étiquetée avec son ou ses niveaux CECR.
- Le professeur peut te demander des sélections transversales : « 3 livres A2-B1 sur la Seconde Guerre mondiale », « des livres audio B1 sur la vie quotidienne »... Filtre par niveau ET par thème en t'appuyant sur les titres et descriptions, et propose le nombre demandé (ou 2-3 par défaut), chacun avec son lien SEUL sur une ligne.
- Si aucune ressource du catalogue ne correspond exactement au thème demandé, dis-le honnêtement et propose ce qui s'en rapproche le plus — n'invente JAMAIS de titre ni de lien.
- Tu peux aussi suggérer une ressource comme support d'activité (lecture à la maison + discussion avec Galia en classe, par exemple).
${lines}`;
}
